/**
 * Wix Dashboard Page
 *
 * Embedded dashboard for Wix users with:
 * - Trial/free tier with one full-power generation
 * - Credits and plan status display
 * - Blog generation interface
 * - Upgrade prompts to Wix Paid Plans
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Zap,
  Crown,
  Gift,
  ChevronRight,
  Check,
  AlertCircle,
  Loader2,
  FileText,
  TrendingUp,
  Clock,
  Star,
  Lock,
  Copy,
  CheckCircle,
  Download,
  History,
  RefreshCw,
  Trash2,
  Image as ImageIcon,
  ExternalLink,
  Building2,
  Palette,
  Users,
  Plus,
  Edit3,
  Eye,
  X,
} from 'lucide-react';
import { marked } from 'marked';
import html2pdf from 'html2pdf.js';
import { useWix, WixProvider } from '../contexts/WixContext';
import ContentSageLogo from '../components/ContentSageLogo';
import { shouldDisableInAppPurchase, openPurchasePage } from '../utils/platform';

// Job status types
type JobStatus = 'queued' | 'processing' | 'generating_content' | 'generating_images' | 'completed' | 'failed';

interface SEOData {
  meta_title?: string;
  meta_description?: string;
  slug?: string;
  focus_keyword_short?: string;
  focus_keyword_long?: string;
  primary_keyword?: string;
  secondary_keywords?: string[];
  long_tail_keywords?: string[];
}

interface StructuredData {
  raw_jsonld?: string;
  article_schema?: Record<string, unknown>;
  faq_schema?: Record<string, unknown>;
}

interface JobState {
  job_id: string;
  status: JobStatus;
  progress_percent: number;
  current_step: string;
  result?: {
    html_content?: string;
    plain_content?: string;
    title?: string;
    meta_description?: string;
    word_count?: number;
    s3_key?: string;
    s3_url?: string;
    images?: Array<{
      url: string;
      s3_url?: string;
      alt?: string;
      prompt?: string;
    }>;
    image_urls?: string[];
    // SEO data
    seo?: SEOData;
    structured_data?: StructuredData;
    open_graph?: {
      og_title?: string;
      og_description?: string;
      twitter_title?: string;
      twitter_description?: string;
    };
    references?: string[];
    excerpt?: string;
    tags?: string[];
    categories?: string[];
  };
  error_message?: string;
  // Top-level S3 info (may come from task state)
  s3_key?: string;
  s3_url?: string;
  // Top-level images (may come from task state)
  images?: Array<{
    url: string;
    s3_url?: string;
    alt?: string;
  }>;
}

// Content history types - ContentPreview matches backend response
interface ContentPreview {
  id: string;
  title: string;
  excerpt: string;
  word_count: number;
  image_count: number;
  created_at: string;
  status: string;
  platforms_published: string[];
}

// Image preferences types
type ImageStyle = 'realistic' | 'illustration' | 'abstract';
type ImagePosition = 'header' | 'inline-30' | 'inline-50' | 'inline-75';
type ColorScheme = 'vibrant' | 'muted' | 'monochrome' | 'brand';

// Individual image slot configuration
interface ImageSlot {
  position: ImagePosition;
  style: ImageStyle;
}

interface ImagePreferences {
  slots: ImageSlot[];        // Configured image slots (each with position + style)
  infographicCount: number;  // Number of infographics (0-2)
  colorScheme: ColorScheme;
}

// Tier-based limits
const TIER_IMAGE_LIMITS: Record<string, number> = {
  free: 1,
  trial: 1,
  starter: 3,
  pro: 3,
  agency: 4,
};

const TIER_WORD_LIMITS: Record<string, number> = {
  free: 1500,
  trial: 1500,
  starter: 2500,
  pro: 4000,
  agency: 5000,
};

const WORD_COUNT_OPTIONS = [
  { value: 800, label: '~800 words (Short)' },
  { value: 1200, label: '~1,200 words (Medium)' },
  { value: 1500, label: '~1,500 words (Standard)' },
  { value: 2000, label: '~2,000 words (Long)' },
  { value: 2500, label: '~2,500 words (Extended)' },
  { value: 3500, label: '~3,500 words (Comprehensive)' },
  { value: 5000, label: '~5,000 words (In-depth)' },
];

// Supported languages for blog generation
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish (Español)' },
  { value: 'fr', label: 'French (Français)' },
  { value: 'de', label: 'German (Deutsch)' },
  { value: 'it', label: 'Italian (Italiano)' },
  { value: 'pt', label: 'Portuguese (Português)' },
  { value: 'nl', label: 'Dutch (Nederlands)' },
  { value: 'pl', label: 'Polish (Polski)' },
  { value: 'ru', label: 'Russian (Русский)' },
  { value: 'ja', label: 'Japanese (日本語)' },
  { value: 'ko', label: 'Korean (한국어)' },
  { value: 'zh', label: 'Chinese (中文)' },
  { value: 'ar', label: 'Arabic (العربية)' },
  { value: 'hi', label: 'Hindi (हिन्दी)' },
  { value: 'tr', label: 'Turkish (Türkçe)' },
  { value: 'vi', label: 'Vietnamese (Tiếng Việt)' },
  { value: 'th', label: 'Thai (ไทย)' },
  { value: 'id', label: 'Indonesian (Bahasa Indonesia)' },
];

// Max infographics - always 2 max (but counts toward total)
const MAX_INFOGRAPHICS = 2;

// Pricing plans to display
const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 29,
    yearlyPrice: 23.20,
    credits: 10,
    features: [
      '10 credits per month',
      'SEO optimization',
      'AI-generated images',
      'Basic analytics',
    ],
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 79,
    yearlyPrice: 63.20,
    credits: 50,
    features: [
      '50 credits per month',
      'Advanced SEO tools',
      'Custom brand voice',
      'Knowledge vault (RAG)',
      'Priority support',
    ],
    popular: true,
  },
  {
    id: 'agency',
    name: 'Agency',
    monthlyPrice: 199,
    yearlyPrice: 159.20,
    credits: 150,
    features: [
      '150 credits per month',
      'Multiple brand profiles',
      'White-label exports',
      'API access',
      'Dedicated support',
    ],
    popular: false,
  },
];

function TrialBanner({ onStartTrial }: { onStartTrial: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-purple-600 to-purple-600 rounded-xl p-6 text-white mb-6"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <Gift className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-xl font-bold mb-1">Try ContentSage Free!</h2>
            <p className="text-purple-100 mb-3">
              Generate one full blog post with all premium features - no credit card required.
              See the quality before you subscribe.
            </p>
            <ul className="flex flex-wrap gap-3 text-sm">
              <li className="flex items-center">
                <Check className="w-4 h-4 mr-1" /> Full 2000+ words
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 mr-1" /> AI images included
              </li>
              <li className="flex items-center">
                <Check className="w-4 h-4 mr-1" /> SEO optimized
              </li>
            </ul>
          </div>
        </div>
        <button
          onClick={onStartTrial}
          className="px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-purple-50 transition-colors flex items-center"
        >
          Start Free Trial
          <ChevronRight className="w-5 h-5 ml-1" />
        </button>
      </div>
    </motion.div>
  );
}

function CreditsDisplay({ credits, plan, onUpgrade }: { credits: number; plan: string; onUpgrade?: () => void }) {
  const planLabel = plan === 'free' ? 'Free' : plan.charAt(0).toUpperCase() + plan.slice(1);
  // Show upgrade for all plans except agency (the highest tier)
  const showUpgrade = plan !== 'agency';
  const isNativeApp = shouldDisableInAppPurchase();

  // Plan-specific styling
  const planStyles = {
    free: {
      bg: 'bg-slate-800/60',
      border: 'border-slate-700/50',
      iconBg: 'bg-slate-700/80',
      iconColor: 'text-slate-400',
      badgeBg: 'bg-slate-700',
      badgeText: 'text-slate-300',
    },
    trial: {
      bg: 'bg-slate-800/60',
      border: 'border-violet-500/30',
      iconBg: 'bg-violet-500/20',
      iconColor: 'text-violet-400',
      badgeBg: 'bg-violet-500/20',
      badgeText: 'text-violet-300',
    },
    starter: {
      bg: 'bg-slate-800/60',
      border: 'border-blue-500/30',
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-400',
      badgeBg: 'bg-blue-500/20',
      badgeText: 'text-blue-300',
    },
    professional: {
      bg: 'bg-slate-800/60',
      border: 'border-violet-500/30',
      iconBg: 'bg-violet-500/20',
      iconColor: 'text-violet-400',
      badgeBg: 'bg-violet-500/20',
      badgeText: 'text-violet-300',
    },
    agency: {
      bg: 'bg-slate-800/60',
      border: 'border-amber-500/30',
      iconBg: 'bg-amber-500/20',
      iconColor: 'text-amber-400',
      badgeBg: 'bg-amber-500/20',
      badgeText: 'text-amber-300',
    },
  };

  const style = planStyles[plan as keyof typeof planStyles] || planStyles.free;

  return (
    <div className={`${style.bg} rounded-2xl px-5 py-3 border ${style.border} backdrop-blur-sm`}>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${style.iconBg} shadow-lg`}>
            <Zap className={`w-5 h-5 ${style.iconColor}`} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Current Plan</p>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold px-2 py-0.5 rounded-md ${style.badgeBg} ${style.badgeText}`}>
                {planLabel}
              </span>
            </div>
          </div>
        </div>
        <div className="h-8 w-px bg-slate-700/50" />
        <div className="text-center">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Credits Remaining</p>
          <p className={`text-2xl font-bold ${style.iconColor}`}>{credits}</p>
        </div>
        {showUpgrade && onUpgrade && !isNativeApp && (
          <button
            onClick={onUpgrade}
            className="ml-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-violet-900/30 hover:shadow-violet-500/40 active:scale-95"
          >
            Upgrade
          </button>
        )}
      </div>
    </div>
  );
}

function PricingCard({
  plan,
  billingCycle,
  onSelect,
}: {
  plan: typeof PLANS[0];
  billingCycle: 'monthly' | 'yearly';
  onSelect: (planId: string) => void;
}) {
  const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className={`relative bg-slate-800/50 rounded-xl p-6 border-2 transition-colors ${
        plan.popular
          ? 'border-purple-500 shadow-lg shadow-purple-500/20'
          : 'border-slate-700/50'
      }`}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-purple-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-white mb-2">
          {plan.name}
        </h3>
        <div className="flex items-baseline justify-center">
          <span className="text-4xl font-bold text-white">
            ${price}
          </span>
          <span className="text-slate-400 ml-1">/mo</span>
        </div>
        {billingCycle === 'yearly' && (
          <p className="text-xs text-green-400 mt-1">
            ${(price * 12).toFixed(2)} billed annually
          </p>
        )}
        <p className="text-sm text-slate-400 mt-1">
          {plan.credits} blog posts/month
        </p>
      </div>

      <ul className="space-y-3 mb-6">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start text-sm">
            <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" />
            <span className="text-slate-300">{feature}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => onSelect(plan.id)}
        className={`w-full py-3 rounded-lg font-semibold transition-colors ${
          plan.popular
            ? 'bg-purple-600 text-white hover:bg-purple-500'
            : 'bg-slate-700 text-white hover:bg-slate-600'
        }`}
      >
        Choose {plan.name}
      </button>
    </motion.div>
  );
}

// Convert markdown to HTML safely
function renderMarkdownToHtml(content: string): string {
  if (!content) return '';

  // Check if content is already HTML (starts with < or has common HTML tags)
  const isHtml = content.trim().startsWith('<') ||
                 /<(p|div|h[1-6]|ul|ol|li|img|a|strong|em)\b/i.test(content);

  if (isHtml) {
    return content;
  }

  // Convert markdown to HTML
  try {
    return marked(content, { breaks: true, gfm: true }) as string;
  } catch {
    return content;
  }
}

// Branding settings for white-label exports (Agency feature)
interface BrandingSettings {
  logoUrl?: string;
  customFooterText?: string;
  brandColors?: {
    primary?: string;
    secondary?: string;
  };
}

// Job Result Display Component with proper formatting
function JobResultDisplay({
  jobState,
  onCopy,
  copied,
  userPlan = 'free',
  brandingSettings,
}: {
  jobState: JobState;
  onCopy: () => void;
  copied: boolean;
  API_BASE?: string;
  instanceToken?: string | null;
  userPlan?: string;
  brandingSettings?: BrandingSettings;
}) {
  const [showFullContent, setShowFullContent] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Get images from various possible locations in the response
  // NOTE: img.url is the presigned URL, img.s3_url is the raw S3 URL
  // Always prefer presigned URL (img.url) for public access
  const images = useMemo(() => {
    const imgs: Array<{ url: string; alt?: string }> = [];

    // Check result.images - prefer presigned URL (url) over raw S3 URL (s3_url)
    if (jobState.result?.images) {
      jobState.result.images.forEach(img => {
        const imageUrl = img.url || img.s3_url;
        if (imageUrl) {
          imgs.push({ url: imageUrl, alt: img.alt || img.prompt });
        }
      });
    }

    // Check result.image_urls (these should already be presigned)
    if (jobState.result?.image_urls) {
      jobState.result.image_urls.forEach(url => {
        if (url && !imgs.find(i => i.url === url)) {
          imgs.push({ url, alt: 'Generated image' });
        }
      });
    }

    // Check top-level images - prefer presigned URL
    if (jobState.images) {
      jobState.images.forEach(img => {
        const imageUrl = img.url || img.s3_url;
        if (imageUrl && !imgs.find(i => i.url === img.url || i.url === img.s3_url)) {
          imgs.push({ url: imageUrl, alt: img.alt });
        }
      });
    }

    return imgs;
  }, [jobState]);

  // Convert content to HTML
  const htmlContent = useMemo(() => {
    return renderMarkdownToHtml(jobState.result?.html_content || '');
  }, [jobState.result?.html_content]);

  // Get S3 URL
  const s3Url = jobState.result?.s3_url || jobState.s3_url;

  // Get filename base from title
  const getFilenameBase = () => {
    return (jobState.result?.title || 'article').replace(/[^a-z0-9]/gi, '-').toLowerCase().slice(0, 50);
  };

  // Download HTML with proper formatting
  const handleDownloadHtml = () => {
    if (jobState.result?.html_content) {
      const isAgency = userPlan === 'agency';
      const hasBranding = isAgency && (brandingSettings?.logoUrl || brandingSettings?.customFooterText);
      const primaryColor = brandingSettings?.brandColors?.primary || '#6366f1';

      // Build logo header
      const logoHtml = hasBranding && brandingSettings?.logoUrl
        ? `<div style="text-align: center; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 2px solid ${primaryColor};"><img src="${brandingSettings.logoUrl}" alt="Logo" style="max-height: 60px; max-width: 200px;"></div>`
        : '';

      // Build footer
      const footerText = hasBranding && brandingSettings?.customFooterText
        ? brandingSettings.customFooterText
        : (isAgency ? '' : 'Generated with ContentSage');
      const footerHtml = footerText
        ? `<footer style="margin-top: 3rem; padding-top: 1rem; border-top: 1px solid #e5e5e5; text-align: center; font-size: 0.875rem; color: #666;">${footerText}</footer>`
        : '';

      const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${jobState.result.title || 'Blog Post'}</title>
  <meta name="description" content="${jobState.result.meta_description || ''}">
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; max-width: 800px; margin: 0 auto; padding: 2rem; line-height: 1.6; }
    img { max-width: 100%; height: auto; border-radius: 8px; margin: 1rem 0; }
    h1, h2, h3 { margin-top: 2rem; }
  </style>
</head>
<body>
${logoHtml}
${htmlContent}
${footerHtml}
</body>
</html>`;

      const blob = new Blob([fullHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${getFilenameBase()}.html`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Helper: Convert HTML to clean text with paragraph spacing
  const htmlToText = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Replace block elements with double newlines
    const blockElements = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, br');
    blockElements.forEach(el => {
      if (el.tagName === 'BR') {
        el.replaceWith('\n');
      } else {
        el.innerHTML = el.innerHTML + '\n\n';
      }
    });

    // Get text and clean up extra whitespace
    let text = tempDiv.textContent || tempDiv.innerText || '';
    text = text.replace(/\n{3,}/g, '\n\n').trim();
    return text;
  };

  // Helper: Convert HTML to Markdown
  const htmlToMarkdown = (html: string): string => {
    let md = html;

    // Convert headings
    md = md.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n');
    md = md.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n');
    md = md.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n');
    md = md.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n');

    // Convert paragraphs
    md = md.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n');

    // Convert bold/italic
    md = md.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
    md = md.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
    md = md.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
    md = md.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');

    // Convert links
    md = md.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');

    // Convert lists
    md = md.replace(/<ul[^>]*>/gi, '\n');
    md = md.replace(/<\/ul>/gi, '\n');
    md = md.replace(/<ol[^>]*>/gi, '\n');
    md = md.replace(/<\/ol>/gi, '\n');
    md = md.replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n');

    // Convert line breaks
    md = md.replace(/<br\s*\/?>/gi, '\n');

    // Remove remaining HTML tags
    md = md.replace(/<[^>]+>/g, '');

    // Clean up whitespace
    md = md.replace(/\n{3,}/g, '\n\n').trim();

    return md;
  };

  // Download as Markdown with full SEO frontmatter
  const handleDownloadMarkdown = () => {
    if (jobState.result?.html_content) {
      const title = jobState.result.title || 'Blog Post';
      const seo = jobState.result.seo || {};
      const tags = jobState.result.tags || [];
      const categories = jobState.result.categories || [];

      // Convert HTML to proper markdown
      const markdownContent = htmlToMarkdown(jobState.result.html_content);

      // Build comprehensive frontmatter
      const frontmatter = [
        '---',
        `title: "${title}"`,
        `meta_title: "${seo.meta_title || title}"`,
        `description: "${seo.meta_description || ''}"`,
        `slug: "${seo.slug || ''}"`,
        `date: ${new Date().toISOString().split('T')[0]}`,
        `focus_keyword: "${seo.focus_keyword_short || ''}"`,
        `long_tail_keyword: "${seo.focus_keyword_long || ''}"`,
      ];

      if (tags.length > 0) {
        frontmatter.push(`tags: [${tags.map(t => `"${t}"`).join(', ')}]`);
      }
      if (categories.length > 0) {
        frontmatter.push(`categories: [${categories.map(c => `"${c}"`).join(', ')}]`);
      }
      if (seo.secondary_keywords?.length) {
        frontmatter.push(`secondary_keywords: [${seo.secondary_keywords.map(k => `"${k}"`).join(', ')}]`);
      }

      frontmatter.push('---');

      // Add structured data as comment if available
      let structuredDataSection = '';
      if (jobState.result.structured_data?.raw_jsonld) {
        structuredDataSection = `\n\n<!-- JSON-LD Structured Data\n${jobState.result.structured_data.raw_jsonld}\n-->\n`;
      }

      const markdown = `${frontmatter.join('\n')}\n\n${markdownContent}${structuredDataSection}`;

      const blob = new Blob([markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${getFilenameBase()}.md`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Download as plain text with SEO metadata header
  const handleDownloadTxt = () => {
    if (jobState.result?.html_content) {
      const title = jobState.result.title || 'Blog Post';
      const seo = jobState.result.seo || {};

      // Convert HTML to text with proper paragraph spacing
      const plainContent = htmlToText(htmlContent);

      // Build metadata header
      const metaHeader = [
        title,
        '='.repeat(title.length),
        '',
        'SEO METADATA',
        '-'.repeat(12),
        `Meta Title: ${seo.meta_title || title}`,
        `Meta Description: ${seo.meta_description || ''}`,
        `Focus Keyword: ${seo.focus_keyword_short || ''}`,
        `Long-tail Keyword: ${seo.focus_keyword_long || ''}`,
        `Slug: ${seo.slug || ''}`,
        '',
        '-'.repeat(50),
        '',
        'ARTICLE CONTENT',
        '-'.repeat(15),
        '',
      ].join('\n');

      const fullText = metaHeader + plainContent;

      const blob = new Blob([fullText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${getFilenameBase()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Download as PDF with SEO metadata (Agency only)
  const handleDownloadPdf = async () => {
    if (!jobState.result?.html_content) return;

    setIsGeneratingPdf(true);
    try {
      const title = jobState.result.title || 'Blog Post';
      const seo = jobState.result.seo || {};
      const isAgency = userPlan === 'agency';
      const hasBranding = isAgency && (brandingSettings?.logoUrl || brandingSettings?.customFooterText);

      // Use custom colors or defaults
      const primaryColor = brandingSettings?.brandColors?.primary || '#6366f1';

      // Build header with optional logo
      let headerHtml = '';
      if (hasBranding && brandingSettings?.logoUrl) {
        headerHtml = `
          <div style="text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid ${primaryColor};">
            <img src="${brandingSettings.logoUrl}" alt="Logo" style="max-height: 60px; max-width: 200px;" />
          </div>
        `;
      }

      // Build footer text
      const footerText = hasBranding && brandingSettings?.customFooterText
        ? brandingSettings.customFooterText
        : (isAgency ? '' : 'Generated with ContentSage');

      // Create a styled container for PDF with SEO metadata section
      const pdfContent = document.createElement('div');
      pdfContent.innerHTML = `
        <div style="font-family: Georgia, serif; max-width: 700px; margin: 0 auto; padding: 40px; line-height: 1.8; color: #333;">
          ${headerHtml}

          <h1 style="font-size: 28px; margin-bottom: 10px; color: #1a1a1a;">${title}</h1>

          <!-- SEO Metadata Section -->
          <div style="background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 30px; font-size: 12px; border-left: 4px solid ${primaryColor};">
            <h3 style="font-size: 14px; margin: 0 0 10px 0; color: #666;">SEO Metadata</h3>
            <p style="margin: 5px 0;"><strong>Meta Title:</strong> ${seo.meta_title || title}</p>
            <p style="margin: 5px 0;"><strong>Meta Description:</strong> ${seo.meta_description || ''}</p>
            <p style="margin: 5px 0;"><strong>Focus Keyword:</strong> ${seo.focus_keyword_short || ''}</p>
            <p style="margin: 5px 0;"><strong>Slug:</strong> ${seo.slug || ''}</p>
          </div>

          <!-- Article Content -->
          <div style="font-size: 14px;">
            ${htmlContent}
          </div>

          ${jobState.result.structured_data?.raw_jsonld ? `
          <!-- Structured Data Section -->
          <div style="background: #f0f0f0; padding: 15px; border-radius: 8px; margin-top: 30px; font-size: 10px; font-family: monospace;">
            <h3 style="font-size: 12px; margin: 0 0 10px 0;">JSON-LD Structured Data</h3>
            <pre style="white-space: pre-wrap; word-break: break-all; margin: 0;">${jobState.result.structured_data.raw_jsonld}</pre>
          </div>
          ` : ''}

          ${footerText ? `
          <div style="margin-top: 40px; padding-top: 15px; border-top: 1px solid #e5e5e5; text-align: center; font-size: 11px; color: #888;">
            ${footerText}
          </div>
          ` : ''}
        </div>
      `;

      document.body.appendChild(pdfContent);

      await html2pdf()
        .set({
          margin: [15, 15, 15, 15],
          filename: `${getFilenameBase()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(pdfContent)
        .save();

      document.body.removeChild(pdfContent);
    } catch (error) {
      console.error('PDF generation failed:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const isAgencyPlan = userPlan === 'agency';

  return (
    <div className="mt-4 space-y-4">
      {/* Success Header */}
      <div className="p-4 bg-green-900/30 rounded-lg border border-green-700/50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Generation Complete!</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onCopy}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={handleDownloadHtml}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors"
              title="Download as HTML"
            >
              <Download className="w-4 h-4" />
              HTML
            </button>
            <button
              onClick={handleDownloadMarkdown}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition-colors"
              title="Download as Markdown"
            >
              <Download className="w-4 h-4" />
              MD
            </button>
            <button
              onClick={handleDownloadTxt}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-600 text-white rounded-lg hover:bg-slate-500 transition-colors"
              title="Download as Plain Text"
            >
              <Download className="w-4 h-4" />
              TXT
            </button>
            {isAgencyPlan && (
              <button
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-500 transition-colors disabled:opacity-50"
                title="Download as PDF (Agency)"
              >
                {isGeneratingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                PDF
              </button>
            )}
            {s3Url && (
              <a
                href={s3Url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
                title="Open in Cloud Storage"
              >
                <ExternalLink className="w-4 h-4" />
                S3
              </a>
            )}
          </div>
        </div>

        {/* Title & Meta */}
        {jobState.result?.title && (
          <p className="text-sm text-green-300 mt-2">
            <strong>Title:</strong> {jobState.result.title}
          </p>
        )}
        {jobState.result?.word_count && (
          <p className="text-xs text-green-400 mt-1">
            {jobState.result.word_count} words generated
          </p>
        )}

        {/* Storage indicator */}
        {(jobState.s3_key || jobState.result?.s3_key) && (
          <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-green-500" />
            Saved to cloud storage
          </p>
        )}
      </div>

      {/* Images Gallery - Show all images with visible download links */}
      {images.length > 0 && (
        <div className="p-4 bg-slate-800/80 rounded-lg border border-slate-700/50">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon className="w-5 h-5 text-purple-400" />
            <h4 className="text-sm font-medium text-slate-300">Generated Images ({images.length})</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {images.map((img, idx) => (
              <div key={idx} className="relative bg-slate-900 rounded-lg border border-slate-600 overflow-hidden">
                {/* Image with placeholder on error */}
                <div className="relative h-36">
                  <img
                    src={img.url}
                    alt={img.alt || `Generated image ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Replace with placeholder instead of hiding
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                  {/* Fallback placeholder (hidden by default) */}
                  <div className="absolute inset-0 bg-slate-700 items-center justify-center text-slate-400 hidden">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                </div>
                {/* Always visible download bar */}
                <div className="flex items-center justify-between p-2 bg-slate-800/90 border-t border-slate-700">
                  <span className="text-xs text-slate-400">Image {idx + 1}</span>
                  <a
                    href={img.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={`image-${idx + 1}.jpg`}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-500 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content Preview */}
      <div className="p-4 bg-slate-800/80 rounded-lg border border-slate-700/50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-slate-400">Article Preview</h4>
          <button
            onClick={() => setShowFullContent(!showFullContent)}
            className="text-xs text-purple-400 hover:text-purple-300"
          >
            {showFullContent ? 'Show less' : 'Show full article'}
          </button>
        </div>
        <div
          className={`prose prose-sm prose-invert max-w-none overflow-hidden transition-all duration-300 ${
            showFullContent ? 'max-h-none' : 'max-h-96'
          }`}
        >
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>
        {!showFullContent && (
          <div className="h-20 bg-gradient-to-t from-slate-800/80 to-transparent -mt-20 relative pointer-events-none" />
        )}
      </div>
    </div>
  );
}

// Position options for images
const POSITION_OPTIONS: { value: ImagePosition; label: string; description: string }[] = [
  { value: 'header', label: 'Header', description: 'Full-width hero image' },
  { value: 'inline-30', label: 'Small', description: '~30% width inline' },
  { value: 'inline-50', label: 'Medium', description: '~50% width inline' },
  { value: 'inline-75', label: 'Large', description: '~75% width inline' },
];

const STYLE_OPTIONS: { value: ImageStyle; label: string; icon: string }[] = [
  { value: 'realistic', label: 'Photo', icon: '📷' },
  { value: 'illustration', label: 'Illustration', icon: '🎨' },
  { value: 'abstract', label: 'Abstract', icon: '✨' },
];

const COLOR_OPTIONS: { value: ColorScheme; label: string; preview: string }[] = [
  { value: 'vibrant', label: 'Vibrant', preview: 'bg-gradient-to-r from-pink-500 to-yellow-500' },
  { value: 'muted', label: 'Muted', preview: 'bg-gradient-to-r from-slate-400 to-stone-400' },
  { value: 'monochrome', label: 'B&W', preview: 'bg-gradient-to-r from-gray-800 to-gray-300' },
  { value: 'brand', label: 'Brand', preview: 'bg-gradient-to-r from-purple-500 to-indigo-500' },
];

// Image Preferences Panel Component - Simplified dropdown-based UI
function ImagePreferencesPanel({
  preferences,
  onChange,
  maxImages,
  disabled,
}: {
  preferences: ImagePreferences;
  onChange: (prefs: ImagePreferences) => void;
  maxImages: number;
  disabled: boolean;
}) {
  // Handle image count change - creates/removes slots as needed
  const handleImageCountChange = (newCount: number) => {
    const currentCount = preferences.slots.length;
    if (newCount === currentCount) return;

    if (newCount > currentCount) {
      // Add new slots
      const newSlots = [...preferences.slots];
      for (let i = currentCount; i < newCount; i++) {
        newSlots.push({
          position: i === 0 ? 'header' : 'inline-50',
          style: 'realistic',
        });
      }
      onChange({ ...preferences, slots: newSlots });
    } else {
      // Remove slots from end
      onChange({ ...preferences, slots: preferences.slots.slice(0, newCount) });
    }
  };

  // Handle infographic count change
  const handleInfographicChange = (newCount: number) => {
    onChange({ ...preferences, infographicCount: newCount });
  };

  // Update a slot's configuration
  const updateSlot = (index: number, updates: Partial<ImageSlot>) => {
    const newSlots = preferences.slots.map((slot, i) =>
      i === index ? { ...slot, ...updates } : slot
    );
    onChange({ ...preferences, slots: newSlots });
  };

  // Calculate max available for each type
  const totalUsed = preferences.slots.length + preferences.infographicCount;
  const maxImagesAllowed = maxImages - preferences.infographicCount;
  const maxInfographicsAllowed = Math.min(MAX_INFOGRAPHICS, maxImages - preferences.slots.length);

  // Generate options for dropdowns
  const imageCountOptions = Array.from({ length: maxImagesAllowed + 1 }, (_, i) => i);
  const infographicCountOptions = Array.from({ length: maxInfographicsAllowed + 1 }, (_, i) => i);

  // Larger touch targets (py-3.5) and improved accessibility
  const selectClassName = "w-full px-4 py-3.5 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 appearance-none cursor-pointer min-h-[44px]";

  return (
    <div className="border border-slate-700 rounded-2xl overflow-hidden bg-slate-900/50">
      {/* Header */}
      <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-800">
        <div className="flex items-center space-x-2.5">
          <div className="p-1.5 bg-violet-500/20 rounded-lg">
            <ImageIcon className="w-4 h-4 text-violet-400" />
          </div>
          <span className="text-sm font-semibold text-slate-200">Image Settings</span>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {/* Top Row: Count Dropdowns */}
        <div className="grid grid-cols-2 gap-4">
          {/* Images Count */}
          <div>
            <label className="block text-xs text-slate-300 mb-2 font-medium">Images</label>
            <div className="relative">
              <select
                value={preferences.slots.length}
                onChange={(e) => handleImageCountChange(parseInt(e.target.value))}
                disabled={disabled}
                className={selectClassName}
              >
                {imageCountOptions.map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'image' : 'images'}</option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
            </div>
          </div>

          {/* Infographics Count */}
          <div>
            <label className="block text-xs text-slate-300 mb-2 font-medium">Infographics</label>
            <div className="relative">
              <select
                value={preferences.infographicCount}
                onChange={(e) => handleInfographicChange(parseInt(e.target.value))}
                disabled={disabled}
                className={selectClassName}
              >
                {infographicCountOptions.map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'infographic' : 'infographics'}</option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Slots remaining indicator */}
        <div className="text-xs text-slate-500 text-center">
          {maxImages - totalUsed} of {maxImages} slots remaining
        </div>

        {/* Individual Image Settings */}
        {preferences.slots.length > 0 && (
          <div className="space-y-4 pt-3 border-t border-slate-800">
            {preferences.slots.map((slot, index) => (
              <div key={index} className="grid grid-cols-3 gap-3 items-end">
                <div className="col-span-1">
                  <label className="block text-[10px] text-slate-500 uppercase tracking-wide mb-1 font-medium">
                    Image {index + 1}
                  </label>
                  <div className="text-xs text-slate-400 py-2">Settings →</div>
                </div>

                {/* Size Dropdown */}
                <div>
                  <label className="block text-[10px] text-slate-500 uppercase tracking-wide mb-1 font-medium">Size</label>
                  <div className="relative">
                    <select
                      value={slot.position}
                      onChange={(e) => updateSlot(index, { position: e.target.value as ImagePosition })}
                      disabled={disabled}
                      className={selectClassName}
                    >
                      {POSITION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
                  </div>
                </div>

                {/* Style Dropdown */}
                <div>
                  <label className="block text-[10px] text-slate-500 uppercase tracking-wide mb-1 font-medium">Style</label>
                  <div className="relative">
                    <select
                      value={slot.style}
                      onChange={(e) => updateSlot(index, { style: e.target.value as ImageStyle })}
                      disabled={disabled}
                      className={selectClassName}
                    >
                      {STYLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
                      ))}
                    </select>
                    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Color Scheme - only show if there are images/infographics */}
        {totalUsed > 0 && (
          <div className="pt-4 border-t border-slate-800">
            <label className="block text-xs text-slate-300 mb-2 font-medium">Color Scheme</label>
            <div className="relative">
              <select
                value={preferences.colorScheme}
                onChange={(e) => onChange({ ...preferences, colorScheme: e.target.value as ColorScheme })}
                disabled={disabled}
                className={selectClassName}
              >
                {COLOR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface BrandProfileOption {
  id: string;
  name: string;
}

function BlogGeneratorSimple({ onGenerate, isGenerating, userPlan, brandProfiles, selectedProfileId, onProfileChange }: {
  onGenerate: (topic: string, imagePrefs: ImagePreferences, wordCount: number, language: string, keywords?: string[], tone?: string, brandProfileId?: string) => void;
  isGenerating: boolean;
  userPlan: string;
  brandProfiles?: BrandProfileOption[];
  selectedProfileId?: string | null;
  onProfileChange?: (profileId: string | null) => void;
}) {
  const [topic, setTopic] = useState('');
  const [language, setLanguage] = useState('en');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [tone, setTone] = useState<string>('professional');
  const maxImages = TIER_IMAGE_LIMITS[userPlan] || 1;
  const maxWords = TIER_WORD_LIMITS[userPlan] || 1500;
  const [wordCount, setWordCount] = useState(1500);
  const isAgency = userPlan === 'agency';
  const hasBrandProfiles = brandProfiles && brandProfiles.length > 0;

  // Show direct input fields when:
  // - Not agency tier, OR
  // - Agency tier but no profile selected
  const showDirectInputs = !isAgency || !selectedProfileId;

  // Default image slots based on tier
  const getDefaultSlots = (max: number): ImageSlot[] => {
    if (max === 0) return [];
    const slots: ImageSlot[] = [{ position: 'header', style: 'realistic' }];
    if (max >= 2) slots.push({ position: 'inline-50', style: 'realistic' });
    if (max >= 3) slots.push({ position: 'inline-50', style: 'realistic' });
    if (max >= 4) slots.push({ position: 'inline-75', style: 'realistic' });
    return slots.slice(0, max);
  };

  const [imagePrefs, setImagePrefs] = useState<ImagePreferences>({
    slots: getDefaultSlots(maxImages),
    infographicCount: 0,
    colorScheme: 'vibrant',
  });

  // Update slots when plan changes
  useEffect(() => {
    const newMaxImages = TIER_IMAGE_LIMITS[userPlan] || 1;
    setImagePrefs(prev => {
      const totalCurrent = prev.slots.length + prev.infographicCount;
      if (totalCurrent <= newMaxImages) return prev;
      // Need to trim - prefer keeping slots over infographics
      const newInfographics = Math.min(prev.infographicCount, Math.max(0, newMaxImages - prev.slots.length));
      const maxSlots = newMaxImages - newInfographics;
      return {
        ...prev,
        slots: prev.slots.slice(0, maxSlots),
        infographicCount: newInfographics,
      };
    });
  }, [userPlan]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (topic.trim()) {
      onGenerate(
        topic,
        imagePrefs,
        wordCount,
        language,
        showDirectInputs ? keywords : undefined,
        showDirectInputs ? tone : undefined,
        selectedProfileId || undefined
      );
    }
  };

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && keywords.length < 5 && !keywords.includes(trimmed)) {
      setKeywords([...keywords, trimmed]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  // Filter word count options to only show what's allowed by tier
  const availableWordOptions = WORD_COUNT_OPTIONS.filter(opt => opt.value <= maxWords);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Brand Profile Selector - Agency Only */}
      {isAgency && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-amber-400" />
            Brand Profile
          </label>
          <div className="relative">
            <select
              value={selectedProfileId || ''}
              onChange={(e) => onProfileChange?.(e.target.value || null)}
              disabled={isGenerating}
              className="w-full px-4 py-3.5 bg-slate-950 border border-amber-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 appearance-none cursor-pointer min-h-[44px]"
            >
              <option value="">No brand profile (default)</option>
              {brandProfiles?.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
            <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400 rotate-90 pointer-events-none" />
          </div>
          {!hasBrandProfiles && (
            <p className="text-xs text-slate-500 mt-1">
              Create brand profiles in Settings to maintain consistent voice across content
            </p>
          )}
          {hasBrandProfiles && selectedProfileId && (
            <p className="text-xs text-amber-400/70 mt-1 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Content will use this brand's tone and style
            </p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          What would you like to write about?
        </label>
        <input
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g., 10 Tips for Better Sleep, How to Start a Garden..."
          className="w-full px-4 py-3.5 border border-slate-700 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 focus:outline-none bg-slate-950 text-white placeholder-slate-500 transition-all"
          disabled={isGenerating}
        />
      </div>

      {/* Word Count Selector */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Article Length
        </label>
        <div className="relative">
          <select
            value={wordCount}
            onChange={(e) => setWordCount(parseInt(e.target.value))}
            disabled={isGenerating}
            className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 appearance-none cursor-pointer"
          >
            {availableWordOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
        </div>
        {maxWords < 5000 && (
          <p className="text-xs text-slate-500 mt-1">
            Upgrade your plan for longer articles (up to 5,000 words)
          </p>
        )}
      </div>

      {/* Language Selector */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Language
        </label>
        <div className="relative">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={isGenerating}
            className="w-full px-4 py-3 bg-slate-950 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 appearance-none cursor-pointer"
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
        </div>
      </div>

      {/* Keywords Input - Show when no profile selected OR not agency */}
      {showDirectInputs && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            Keywords
            <span className="text-xs text-slate-500 font-normal">(Optional, max 5)</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={handleKeywordKeyPress}
              placeholder="Add a keyword..."
              disabled={isGenerating || keywords.length >= 5}
              className="flex-1 px-4 py-3 bg-slate-950 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 placeholder-slate-500 transition-all disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleAddKeyword}
              disabled={isGenerating || keywords.length >= 5 || !keywordInput.trim()}
              className="px-4 py-3 bg-violet-600 hover:bg-violet-500 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          {keywords.length > 0 ? (
            <div className="flex flex-wrap gap-2 mt-2">
              {keywords.map((kw, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-violet-900/30 border border-violet-500/30 rounded-full text-sm text-violet-200"
                >
                  {kw}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(i)}
                    disabled={isGenerating}
                    className="ml-1 hover:text-violet-100 disabled:opacity-50"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500 mt-1">No keywords added yet.</p>
          )}
        </div>
      )}

      {/* Tone of Voice Selector - Show when no profile selected OR not agency */}
      {showDirectInputs && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
            <Palette className="w-4 h-4 text-purple-400" />
            Tone of Voice
          </label>
          <div className="flex flex-wrap gap-2">
            {['Professional', 'Witty', 'Academic', 'Casual', 'Persuasive', 'Conversational'].map((toneOption) => (
              <button
                key={toneOption}
                type="button"
                onClick={() => setTone(toneOption.toLowerCase())}
                disabled={isGenerating}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  tone === toneOption.toLowerCase()
                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-900/30'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {toneOption}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Image Preferences Panel */}
      <ImagePreferencesPanel
        preferences={imagePrefs}
        onChange={setImagePrefs}
        maxImages={maxImages}
        disabled={isGenerating}
      />

      <button
        type="submit"
        disabled={!topic.trim() || isGenerating}
        className="w-full py-3.5 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all shadow-lg shadow-violet-900/30 hover:shadow-violet-500/40 active:scale-[0.99]"
      >
        {isGenerating ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Generating your blog post...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 mr-2" />
            Generate Blog Post
          </>
        )}
      </button>
    </form>
  );
}

function UpgradePrompt({ onViewPlans }: { onViewPlans: () => void }) {
  const isNativeApp = shouldDisableInAppPurchase();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 rounded-xl p-6 border border-amber-700/50"
    >
      <div className="flex items-start space-x-4">
        <div className="p-3 bg-amber-900/50 rounded-lg">
          <Crown className="w-6 h-6 text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-1">
            You're out of credits
          </h3>
          {isNativeApp ? (
            <>
              <p className="text-slate-300 mb-4">
                To upgrade your plan, please visit contentsage.app on your desktop browser.
              </p>
              <button
                onClick={() => openPurchasePage()}
                className="px-4 py-2 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-400 transition-colors"
              >
                Open in Browser
              </button>
            </>
          ) : (
            <>
              <p className="text-slate-300 mb-4">
                Upgrade to keep creating amazing content. Plans start at just $29/month.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onViewPlans}
                  className="px-4 py-2 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-400 transition-colors"
                >
                  View Plans
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function WixDashboardContent() {
  const { userData, isLoading, error, canUseTrial, hasCredits, useTrialCredit, instanceToken, refreshUserData, trackBiEvent } = useWix();
  const [showPricing, setShowPricing] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);
  const [showTrialBanner, setShowTrialBanner] = useState(true);

  // Helper to show pricing modal with BI tracking
  const handleShowPricing = useCallback(() => {
    trackBiEvent('upgrade_prompted', {
      current_plan: userData?.plan || 'free',
      credits_remaining: userData?.credits || 0,
      trigger: 'user_action',
    });
    setShowPricing(true);
  }, [trackBiEvent, userData?.plan, userData?.credits]);

  // Job polling state
  const [jobState, setJobState] = useState<JobState | null>(null);
  const [copied, setCopied] = useState(false);
  const pollIntervalRef = useRef<number | null>(null);

  // Content history state
  const [contentHistory, setContentHistory] = useState<ContentPreview[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [downloadingZipId, setDownloadingZipId] = useState<string | null>(null);

  // Preview modal state
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Check if user has a paid plan (ZIP download requires paid plan)
  const isPaidPlan = ['starter', 'pro', 'agency'].includes(userData?.plan || '');

  // Branding settings for white-label exports (Agency feature)
  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings | undefined>(undefined);

  // Brand profiles management (Agency feature)
  interface BrandProfile {
    id: string;
    name: string;
    description?: string;
    industry?: string;
    tone: string;
    style?: string;
    target_audience?: string;
    keywords?: string[];
    avoid_topics?: string[];
    logo_url?: string;
    custom_footer_text?: string;
  }

  interface ProfileFormData {
    name: string;
    description: string;
    industry: string;
    tone: string;
    style: string;
    target_audience: string;
    keywords: string;
    avoid_topics: string;
    custom_footer_text: string;
  }

  const emptyProfileForm: ProfileFormData = {
    name: '',
    description: '',
    industry: '',
    tone: 'professional',
    style: '',
    target_audience: '',
    keywords: '',
    avoid_topics: '',
    custom_footer_text: '',
  };

  const [brandProfiles, setBrandProfiles] = useState<BrandProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [showBrandProfiles, setShowBrandProfiles] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [profileForm, setProfileForm] = useState<ProfileFormData>(emptyProfileForm);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

  // Fetch content history
  const fetchContentHistory = async () => {
    if (!instanceToken) return;

    setIsLoadingHistory(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/wix/blog/history?limit=50`, {
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContentHistory(data || []);
      } else {
        console.error('Failed to fetch history:', response.status);
      }
    } catch (err) {
      console.error('History fetch error:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch history on mount and when a job completes
  useEffect(() => {
    if (instanceToken) {
      fetchContentHistory();
    }
  }, [instanceToken]);

  // Refresh history when a job completes
  useEffect(() => {
    if (jobState?.status === 'completed') {
      // Delay slightly to ensure S3 upload is complete
      setTimeout(fetchContentHistory, 2000);
    }
  }, [jobState?.status]);

  // Fetch brand profiles and branding settings for Agency users
  const fetchBrandProfiles = useCallback(async () => {
    if (userData?.plan !== 'agency' || !instanceToken) {
      setBrandProfiles([]);
      setBrandingSettings(undefined);
      return;
    }

    setIsLoadingProfiles(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/blog/brand-profiles`, {
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const profiles = data.profiles || [];
        setBrandProfiles(profiles);

        // Get default profile and set branding
        const defaultProfileId = localStorage.getItem('defaultBrandProfileId');
        if (defaultProfileId) {
          setSelectedProfileId(defaultProfileId);
          const defaultProfile = profiles.find((p: BrandProfile) => p.id === defaultProfileId);
          if (defaultProfile && (defaultProfile.logo_url || defaultProfile.custom_footer_text)) {
            setBrandingSettings({
              logoUrl: defaultProfile.logo_url || undefined,
              customFooterText: defaultProfile.custom_footer_text || undefined,
              brandColors: undefined, // Will be added when profile has it
            });
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch brand profiles:', err);
    } finally {
      setIsLoadingProfiles(false);
    }
  }, [userData?.plan, instanceToken, API_BASE]);

  useEffect(() => {
    fetchBrandProfiles();
  }, [fetchBrandProfiles]);

  // Track app loaded event once per user session
  const appLoadedFired = useRef(false);
  useEffect(() => {
    if (userData && !isLoading && !appLoadedFired.current) {
      appLoadedFired.current = true;
      trackBiEvent('app_loaded', {
        plan: userData.plan,
        credits: userData.credits,
        has_credits: userData.credits > 0,
        is_trial: userData.isTrial,
      });
    }
  }, [userData, isLoading, trackBiEvent]);

  // Track credit shortage event once when user runs out of credits
  const creditShortageFired = useRef(false);
  useEffect(() => {
    if (userData && !isLoading && !hasCredits && !canUseTrial && !creditShortageFired.current) {
      creditShortageFired.current = true;
      trackBiEvent('credit_shortage', {
        plan: userData.plan,
        credits_remaining: 0,
      });
    }
    // Reset if credits become available again
    if (hasCredits || canUseTrial) {
      creditShortageFired.current = false;
    }
  }, [userData, isLoading, hasCredits, canUseTrial, trackBiEvent]);

  // Set a profile as default for white-label exports
  const handleSetDefaultProfile = (profileId: string | null) => {
    if (profileId) {
      localStorage.setItem('defaultBrandProfileId', profileId);
      setSelectedProfileId(profileId);
      const profile = brandProfiles.find(p => p.id === profileId);
      if (profile) {
        setBrandingSettings({
          logoUrl: profile.logo_url || undefined,
          customFooterText: profile.custom_footer_text || undefined,
          brandColors: undefined,
        });
      }
    } else {
      localStorage.removeItem('defaultBrandProfileId');
      setSelectedProfileId(null);
      setBrandingSettings(undefined);
    }
  };

  // Create or update a brand profile
  const handleSaveProfile = async () => {
    if (!instanceToken || !profileForm.name.trim()) {
      setProfileError('Profile name is required');
      return;
    }

    setIsSavingProfile(true);
    setProfileError(null);

    try {
      const url = editingProfileId
        ? `${API_BASE}/api/v1/blog/brand-profiles/${editingProfileId}`
        : `${API_BASE}/api/v1/blog/brand-profiles`;

      const method = editingProfileId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: profileForm.name.trim(),
          description: profileForm.description.trim() || null,
          industry: profileForm.industry.trim() || null,
          tone: profileForm.tone,
          style: profileForm.style.trim() || null,
          target_audience: profileForm.target_audience.trim() || null,
          keywords: profileForm.keywords ? profileForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
          avoid_topics: profileForm.avoid_topics ? profileForm.avoid_topics.split(',').map(t => t.trim()).filter(Boolean) : [],
          custom_footer_text: profileForm.custom_footer_text.trim() || null,
        }),
      });

      if (response.ok) {
        // Refresh the list
        await fetchBrandProfiles();
        // Reset form
        setShowProfileForm(false);
        setEditingProfileId(null);
        setProfileForm(emptyProfileForm);
      } else {
        const error = await response.json();
        setProfileError(error.detail || 'Failed to save profile');
      }
    } catch (err) {
      setProfileError('Network error. Please try again.');
      console.error('Save profile error:', err);
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Delete a brand profile
  const handleDeleteProfile = async (profileId: string) => {
    if (!instanceToken) return;

    if (!window.confirm('Are you sure you want to delete this profile?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/blog/brand-profiles/${profileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
        },
      });

      if (response.ok) {
        // If deleting the selected profile, clear selection
        if (selectedProfileId === profileId) {
          handleSetDefaultProfile(null);
        }
        // Refresh the list
        await fetchBrandProfiles();
      } else {
        console.error('Failed to delete profile');
      }
    } catch (err) {
      console.error('Delete profile error:', err);
    }
  };

  // Edit a brand profile
  const handleEditProfile = (profile: BrandProfile) => {
    setEditingProfileId(profile.id);
    setProfileForm({
      name: profile.name,
      description: profile.description || '',
      industry: profile.industry || '',
      tone: profile.tone,
      style: profile.style || '',
      target_audience: profile.target_audience || '',
      keywords: profile.keywords?.join(', ') || '',
      avoid_topics: profile.avoid_topics?.join(', ') || '',
      custom_footer_text: profile.custom_footer_text || '',
    });
    setLogoPreview(profile.logo_url || null);
    setShowProfileForm(true);
    setProfileError(null);
  };

  // Start creating a new profile
  const handleCreateNewProfile = () => {
    setEditingProfileId(null);
    setProfileForm(emptyProfileForm);
    setShowProfileForm(true);
    setProfileError(null);
  };

  // Cancel profile form
  const handleCancelProfileForm = () => {
    setShowProfileForm(false);
    setEditingProfileId(null);
    setProfileForm(emptyProfileForm);
    setProfileError(null);
    setLogoPreview(null);
  };

  // Upload logo for a brand profile
  const handleLogoUpload = async (file: File) => {
    if (!editingProfileId || !instanceToken) {
      setProfileError('Please save the profile first before uploading a logo');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setProfileError('Invalid file type. Please upload PNG, JPEG, SVG, or WebP');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setProfileError('File too large. Maximum size is 2MB');
      return;
    }

    setIsUploadingLogo(true);
    setProfileError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${API_BASE}/api/v1/blog/brand-profiles/${editingProfileId}/logo`,
        {
          method: 'POST',
          headers: {
            'Authorization': `WixInstance ${instanceToken}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLogoPreview(data.logo_url);
        // Refresh profiles to get updated logo URL
        await fetchBrandProfiles();
      } else {
        const error = await response.json();
        setProfileError(error.detail || 'Failed to upload logo');
      }
    } catch (err) {
      setProfileError('Network error. Please try again.');
      console.error('Logo upload error:', err);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Delete logo from a brand profile
  const handleDeleteLogo = async () => {
    if (!editingProfileId || !instanceToken) return;

    setIsUploadingLogo(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/blog/brand-profiles/${editingProfileId}/logo`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `WixInstance ${instanceToken}`,
          },
        }
      );

      if (response.ok) {
        setLogoPreview(null);
        await fetchBrandProfiles();
      }
    } catch (err) {
      console.error('Logo delete error:', err);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Poll for job status
  useEffect(() => {
    if (!jobState?.job_id || jobState.status === 'completed' || jobState.status === 'failed') {
      return;
    }

    const pollJobStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/jobs/${jobState.job_id}`, {
          headers: {
            'Authorization': `WixInstance ${instanceToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setJobState(data);

          if (data.status === 'completed' || data.status === 'failed') {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            setIsGenerating(false);

            // Refresh user data to update credits
            if (data.status === 'completed') {
              await refreshUserData();
              // Track successful blog generation completion
              trackBiEvent('blog_generation_completed', {
                topic: data.result?.title?.slice(0, 100) || 'unknown',
                success: true,
                has_images: (data.result?.images?.length || 0) > 0,
              });
            } else if (data.status === 'failed') {
              // Track failed blog generation
              trackBiEvent('blog_generation_failed', {
                topic: 'unknown',
                error_type: 'job_failed',
                error_message: (data.error_message || 'Job failed').slice(0, 200),
              });
            }
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };

    // Start polling
    pollIntervalRef.current = window.setInterval(pollJobStatus, 2000);

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [jobState?.job_id, jobState?.status, instanceToken, API_BASE, refreshUserData]);

  // Copy to clipboard
  const handleCopyContent = async () => {
    if (jobState?.result?.html_content) {
      await navigator.clipboard.writeText(jobState.result.html_content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Download content in specified format
  const handleDownloadContent = async (contentId: string, format: 'html' | 'markdown' | 'json') => {
    if (!instanceToken) return;

    try {
      const response = await fetch(`${API_BASE}/api/v1/wix/content/download/${contentId}?format=${format}`, {
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
        },
      });

      if (response.ok) {
        // Trigger browser download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `content-${contentId.slice(0, 8)}.${format === 'markdown' ? 'md' : format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Download failed:', response.status);
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  // Download all content as ZIP
  const handleDownloadZip = async (contentId: string) => {
    if (!instanceToken) return;

    setDownloadingZipId(contentId);
    try {
      const response = await fetch(`${API_BASE}/api/v1/wix/content/download-zip/${contentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Open the presigned URL to trigger download
        if (data.download_url) {
          window.open(data.download_url, '_blank');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('ZIP download failed:', response.status, errorData);
        alert(`Failed to generate ZIP: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('ZIP download error:', err);
      alert('Failed to generate ZIP. Please try again.');
    } finally {
      setDownloadingZipId(null);
    }
  };

  // Delete content from history
  const handleDeleteContent = async (jobId: string) => {
    if (!instanceToken) {
      alert('Authentication required. Please refresh the page.');
      return;
    }

    if (!confirm('Are you sure you want to delete this content?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/v1/storage/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
        },
      });

      if (response.ok) {
        // Remove from local state
        setContentHistory(prev => prev.filter(item => item.id !== jobId));
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to delete content: ${errorData.detail || 'Unknown error'}`);
        console.error('Delete failed:', response.status, errorData);
      }
    } catch (err) {
      alert('Failed to delete content. Please try again.');
      console.error('Delete error:', err);
    }
  };

  // Preview article content
  const handlePreviewContent = async (contentId: string, title: string) => {
    if (!instanceToken) {
      alert('Authentication required. Please refresh the page.');
      return;
    }

    setIsLoadingPreview(true);
    setPreviewTitle(title);
    setPreviewContent(null);

    try {
      const response = await fetch(`${API_BASE}/api/v1/wix/content/download/${contentId}?format=html`, {
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
        },
      });

      if (response.ok) {
        const htmlContent = await response.text();
        setPreviewContent(htmlContent);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to load preview: ${errorData.detail || 'Unknown error'}`);
        setPreviewContent(null);
      }
    } catch (err) {
      alert('Failed to load preview. Please try again.');
      console.error('Preview error:', err);
      setPreviewContent(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewContent(null);
    setPreviewTitle('');
  };

  const handleGenerate = async (topic: string, imagePrefs: ImagePreferences, wordCount: number, language: string, keywords?: string[], tone?: string, brandProfileId?: string) => {
    if (!instanceToken) return;

    setIsGenerating(true);
    setGeneratedContent(null);

    // Track blog generation initiated
    trackBiEvent('blog_generation_initiated', {
      topic: topic.slice(0, 100),
      word_count: wordCount,
      language: language,
      has_images: imagePrefs.slots.length > 0,
      has_infographics: imagePrefs.infographicCount > 0,
      brand_profile_id: brandProfileId || 'none',
      keywords_count: keywords?.length || 0,
      tone: tone || 'default',
    });

    try {
      // Use trial credit if needed
      if (canUseTrial && !hasCredits) {
        await useTrialCredit();
        // Track trial credit usage
        trackBiEvent('trial_credit_used', {
          credits_remaining: (userData?.trialCreditsRemaining || 0) - 1,
        });
      }

      // Prepare image slots data for API
      const totalImages = imagePrefs.slots.length + imagePrefs.infographicCount;

      // Build request body
      const requestBody: Record<string, unknown> = {
        topic,
        user_plan: userData?.plan || 'free',  // Pass user's subscription plan
        // Word count from user selection - backend will apply tier limits
        target_word_count: wordCount,
        // Language for content generation
        target_language: language,
        // Image preferences from user - new per-image structure
        generate_images: totalImages > 0,
        image_count: imagePrefs.slots.length,           // Regular images only
        infographic_count: imagePrefs.infographicCount,
        image_slots: imagePrefs.slots.map(slot => ({
          position: slot.position,
          style: slot.style,
        })),
        image_color_scheme: imagePrefs.colorScheme,
      };

      // Add keywords and tone if provided (direct input)
      if (keywords && keywords.length > 0) {
        requestBody.keywords = keywords;
      }
      if (tone) {
        requestBody.tone = tone;
      }

      // Add brand profile if selected (Agency feature)
      if (brandProfileId) {
        requestBody.brand_profile_id = brandProfileId;
      }

      const response = await fetch(`${API_BASE}/api/v1/blog/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const data = await response.json();
        // Start job polling
        if (data.job_id) {
          setJobState({
            job_id: data.job_id,
            status: 'queued',
            progress_percent: 0,
            current_step: 'Starting generation...',
          });
        }
        setGeneratedContent(null);
        setShowTrialBanner(false);
      } else {
        console.error('Generation response not OK:', response.status, response.statusText);
        try {
          const errorData = await response.json();
          console.error('Error data:', errorData);
          // Handle both string and object detail fields
          let errorMessage = 'Generation failed';
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            } else if (errorData.detail.message) {
              errorMessage = errorData.detail.message;
            } else if (errorData.detail.error) {
              errorMessage = errorData.detail.error;
            }
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
          setGeneratedContent(`Error: ${errorMessage}`);
          // Track generation failure
          trackBiEvent('blog_generation_failed', {
            topic: topic.slice(0, 100),
            error_type: 'api_error',
            error_message: errorMessage.slice(0, 200),
          });
        } catch {
          const textError = await response.text();
          console.error('Error text:', textError);
          setGeneratedContent(`Error: ${response.status} - ${response.statusText}`);
          // Track generation failure
          trackBiEvent('blog_generation_failed', {
            topic: topic.slice(0, 100),
            error_type: 'parse_error',
            error_message: `${response.status} - ${response.statusText}`.slice(0, 200),
          });
        }
      }
    } catch (err) {
      console.error('Generation error:', err);
      setGeneratedContent(`Error: ${err instanceof Error ? err.message : 'Failed to connect to server'}`);
      // Track generation failure
      trackBiEvent('blog_generation_failed', {
        topic: topic.slice(0, 100),
        error_type: 'network_error',
        error_message: (err instanceof Error ? err.message : 'Failed to connect to server').slice(0, 200),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectPlan = useCallback(async (planId: string) => {
    // Wix App ID from wix.config.json
    const WIX_APP_ID = '1f9c21c4-d736-40a1-ba0e-e8ade4a764e1';

    // Map frontend plan IDs to Wix Product IDs (from Wix Dev Center)
    const wixPlanMapping: Record<string, string> = {
      'starter': 'starter',
      'pro': 'pro',
      'agency': 'agency-plan',
    };

    const wixPlanId = wixPlanMapping[planId] || planId;
    console.log('Selected plan:', planId, '-> Wix plan:', wixPlanId);

    // Track plan selection BI event
    trackBiEvent('plan_selected', {
      plan_id: planId,
      wix_plan_id: wixPlanId,
      current_plan: userData?.plan || 'free',
    });

    // Extract instance ID from token for redirect URL
    let instanceId = '';
    try {
      if (instanceToken) {
        const parts = instanceToken.split('.');
        if (parts.length === 2) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          instanceId = payload.instanceId || '';
        }
      }
    } catch (e) {
      console.log('Could not parse instance ID from token');
    }

    try {
      // Call our backend to get Wix checkout URL
      const response = await fetch(`${API_BASE}/api/v1/wix/billing/checkout-url`, {
        method: 'POST',
        headers: {
          'Authorization': `WixInstance ${instanceToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: wixPlanId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          // Redirect to Wix checkout
          window.location.href = data.checkout_url;
          return;
        }
      }

      // If API call fails, redirect to Wix app upgrade page
      console.log('API checkout failed, redirecting to Wix upgrade page');

      // Wix standard upgrade URL
      const upgradeUrl = instanceId
        ? `https://www.wix.com/apps/upgrade/${WIX_APP_ID}?appInstanceId=${instanceId}`
        : `https://www.wix.com/apps/upgrade/${WIX_APP_ID}`;

      window.open(upgradeUrl, '_blank');

    } catch (error) {
      console.error('Error getting checkout URL:', error);

      // Fallback: Open Wix upgrade page in new tab
      const upgradeUrl = instanceId
        ? `https://www.wix.com/apps/upgrade/${WIX_APP_ID}?appInstanceId=${instanceId}`
        : `https://www.wix.com/apps/upgrade/${WIX_APP_ID}`;

      window.open(upgradeUrl, '_blank');
    }

    setShowPricing(false);
  }, [instanceToken, API_BASE]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ContentSageLogo size={40} showText={false} />
              <div className="ml-2">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-amber-400 bg-clip-text text-transparent">ContentSage</h1>
                <p className="text-sm text-slate-400">AI Blog Generator</p>
              </div>
            </div>

            {userData && (
              <CreditsDisplay credits={userData.credits} plan={userData.plan} onUpgrade={handleShowPricing} />
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {showPricing ? (
            <motion.div
              key="pricing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Choose Your Plan
                </h2>
                <button
                  onClick={() => setShowPricing(false)}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  Back to Generator
                </button>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                1 credit = 1 AI-generated blog post with SEO optimization and images
              </p>

              {/* Billing Cycle Toggle */}
              <div className="flex items-center justify-center mb-6 space-x-3">
                <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white font-semibold' : 'text-slate-400'}`}>
                  Monthly
                </span>
                <button
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                  className="relative w-14 h-7 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all"
                >
                  <motion.div
                    animate={{ x: billingCycle === 'monthly' ? 2 : 30 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-lg"
                  />
                </button>
                <span className={`text-sm ${billingCycle === 'yearly' ? 'text-white font-semibold' : 'text-slate-400'}`}>
                  Yearly
                </span>
                {billingCycle === 'yearly' && (
                  <span className="ml-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">
                    Save 20%
                  </span>
                )}
              </div>

              {shouldDisableInAppPurchase() ? (
                <div className="bg-slate-800/50 rounded-xl p-8 border border-slate-700/50 text-center">
                  <Crown className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Upgrade on Desktop
                  </h3>
                  <p className="text-slate-300 mb-6 max-w-md mx-auto">
                    To subscribe to a plan, please visit contentsage.app on your desktop browser.
                    Your subscription will sync automatically to this app.
                  </p>
                  <button
                    onClick={() => openPurchasePage()}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-500 hover:to-blue-500 transition-colors"
                  >
                    Open Website in Browser
                  </button>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-6">
                  {PLANS.map((plan) => (
                    <PricingCard
                      key={plan.id}
                      plan={plan}
                      billingCycle={billingCycle}
                      onSelect={handleSelectPlan}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="generator"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Trial Banner */}
              {canUseTrial && showTrialBanner && (
                <TrialBanner onStartTrial={() => setShowTrialBanner(false)} />
              )}

              {/* No Credits Warning */}
              {!hasCredits && !canUseTrial && (
                <UpgradePrompt
                  onViewPlans={handleShowPricing}
                />
              )}

              {/* Generator Card */}
              <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 hover:border-violet-500/20 transition-colors">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-violet-500/20 rounded-xl">
                    <FileText className="w-5 h-5 text-violet-400" />
                  </div>
                  <h2 className="text-lg font-bold text-white tracking-tight">
                    Generate Blog Post
                  </h2>
                </div>

                <BlogGeneratorSimple
                  onGenerate={handleGenerate}
                  isGenerating={isGenerating}
                  userPlan={userData?.plan || 'free'}
                  brandProfiles={brandProfiles}
                  selectedProfileId={selectedProfileId}
                  onProfileChange={setSelectedProfileId}
                />

                {/* Job Progress */}
                {jobState && jobState.status !== 'completed' && jobState.status !== 'failed' && (
                  <div className="mt-6 p-5 bg-violet-500/5 rounded-2xl border border-violet-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-violet-500/20 rounded-lg">
                        <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
                      </div>
                      <div>
                        <span className="font-semibold text-white block">
                          {jobState.current_step || 'Generating...'}
                        </span>
                        <span className="text-xs text-slate-500">Please wait while we create your content</span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-violet-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${jobState.progress_percent || 10}%` }}
                      />
                    </div>
                    <p className="text-xs text-violet-400 mt-2 font-mono">
                      {jobState.progress_percent || 0}% complete
                    </p>
                  </div>
                )}

                {/* Job Failed */}
                {jobState?.status === 'failed' && (
                  <div className="mt-6 p-5 bg-red-500/5 rounded-2xl border border-red-500/20">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-500/20 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                      </div>
                      <div>
                        <span className="font-semibold text-red-400 block">Generation Failed</span>
                        <p className="text-sm text-slate-400 mt-1">
                          {jobState.error_message || 'An error occurred during generation.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Job Completed - Show Result */}
                {jobState?.status === 'completed' && jobState.result && (
                  <JobResultDisplay
                    jobState={jobState}
                    onCopy={handleCopyContent}
                    copied={copied}
                    userPlan={userData?.plan || 'free'}
                    brandingSettings={brandingSettings}
                  />
                )}

                {/* Simple message display (for errors, resets, etc.) */}
                {generatedContent && !jobState && (
                  <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    <p className="text-sm text-slate-300">{generatedContent}</p>
                  </div>
                )}
              </div>

              {/* Content History Section */}
              <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 hover:border-violet-500/20 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-violet-500/20 rounded-xl">
                      <History className="w-5 h-5 text-violet-400" />
                    </div>
                    <h2 className="text-lg font-bold text-white tracking-tight">
                      Content History
                    </h2>
                    {contentHistory.length > 0 && (
                      <span className="text-xs font-mono bg-violet-500/20 text-violet-300 px-2.5 py-1 rounded-lg border border-violet-500/20">
                        {contentHistory.length}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={fetchContentHistory}
                    disabled={isLoadingHistory}
                    className="p-2 hover:bg-slate-800 rounded-xl transition-colors border border-transparent hover:border-slate-700"
                    title="Refresh history"
                  >
                    <RefreshCw className={`w-5 h-5 text-slate-400 ${isLoadingHistory ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {isLoadingHistory && contentHistory.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                    <span className="ml-3 text-slate-400">Loading history...</span>
                  </div>
                ) : contentHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-slate-800/80 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-slate-600" />
                    </div>
                    <p className="text-slate-300 font-medium">No content generated yet</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Generate your first blog post above!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contentHistory.slice(0, showHistory ? undefined : 3).map((item) => {
                      // Estimate file size from word count (roughly 6 bytes per word in HTML)
                      const estimatedSize = Math.round((item.word_count * 6) / 1024);
                      const createdDate = new Date(item.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      });

                      return (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700/30 hover:border-slate-600/50 transition-colors"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="p-2 bg-purple-900/30 rounded-lg">
                              <FileText className="w-5 h-5 text-purple-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-white truncate">
                                {item.title}
                              </p>
                              <p className="text-xs text-slate-400">
                                {createdDate} • {item.word_count} words • ~{estimatedSize} KB
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handlePreviewContent(item.id, item.title)}
                              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
                              title="Preview"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <div className="relative group">
                              <button
                                className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 transition-colors"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              {/* Download dropdown */}
                              <div className="absolute right-0 mt-1 w-40 bg-slate-800 border border-slate-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                <button
                                  onClick={() => handleDownloadContent(item.id, 'html')}
                                  className="w-full px-3 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 hover:text-white transition-colors rounded-t-lg"
                                >
                                  HTML
                                </button>
                                <button
                                  onClick={() => handleDownloadContent(item.id, 'markdown')}
                                  className="w-full px-3 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                                >
                                  Markdown
                                </button>
                                <button
                                  onClick={() => handleDownloadContent(item.id, 'json')}
                                  className={`w-full px-3 py-2 text-sm text-left text-slate-300 hover:bg-slate-700 hover:text-white transition-colors ${!isPaidPlan ? 'rounded-b-lg' : ''}`}
                                >
                                  JSON (Full)
                                </button>
                                {isPaidPlan && (
                                  <>
                                    <div className="border-t border-slate-700 my-1" />
                                    <button
                                      onClick={() => handleDownloadZip(item.id)}
                                      disabled={downloadingZipId === item.id}
                                      className="w-full px-3 py-2 text-sm text-left text-purple-400 hover:bg-purple-600 hover:text-white transition-colors rounded-b-lg flex items-center gap-2 disabled:opacity-50"
                                    >
                                      {downloadingZipId === item.id ? (
                                        <>
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                          Generating...
                                        </>
                                      ) : (
                                        <>All (ZIP)</>
                                      )}
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteContent(item.id)}
                              className="p-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-red-600 hover:text-white transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {contentHistory.length > 3 && (
                      <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="w-full py-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        {showHistory ? 'Show less' : `Show ${contentHistory.length - 3} more`}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Agency Features Section - Only for Agency plan */}
              {userData?.plan === 'agency' && (
                <div className="bg-gradient-to-br from-amber-500/5 via-slate-900 to-violet-500/5 rounded-2xl p-6 border border-amber-500/20">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2.5 bg-amber-500/20 rounded-xl shadow-lg shadow-amber-900/20">
                        <Crown className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-white tracking-tight">Agency Features</h2>
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">White-label exports & brand management</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowBrandProfiles(!showBrandProfiles)}
                      className="text-sm text-amber-400 hover:text-amber-300 flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg border border-amber-500/20 transition-colors"
                    >
                      {showBrandProfiles ? 'Hide' : 'Manage'} Profiles
                      <ChevronRight className={`w-4 h-4 transition-transform ${showBrandProfiles ? 'rotate-90' : ''}`} />
                    </button>
                  </div>

                  {/* Brand Profiles Manager - Collapsible */}
                  <AnimatePresence>
                    {showBrandProfiles && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden mb-4"
                      >
                        <div className="bg-slate-900/80 rounded-2xl p-5 border border-slate-700/50">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-white flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-amber-400" />
                              Brand Profiles
                            </h3>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handleCreateNewProfile}
                                className="text-xs bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 shadow-lg shadow-amber-900/20"
                              >
                                <Plus className="w-3 h-3" />
                                New
                              </button>
                              <button
                                onClick={fetchBrandProfiles}
                                disabled={isLoadingProfiles}
                                className="p-1 hover:bg-slate-700 rounded transition-colors"
                              >
                                <RefreshCw className={`w-4 h-4 text-slate-400 ${isLoadingProfiles ? 'animate-spin' : ''}`} />
                              </button>
                            </div>
                          </div>

                          {/* Create/Edit Form */}
                          {showProfileForm && (
                            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600 mb-4">
                              <h4 className="text-sm font-medium text-white mb-3">
                                {editingProfileId ? 'Edit Profile' : 'Create New Profile'}
                              </h4>

                              {profileError && (
                                <div className="bg-red-900/30 border border-red-700 text-red-300 text-xs p-2 rounded mb-3">
                                  {profileError}
                                </div>
                              )}

                              <div className="space-y-3">
                                {/* Name */}
                                <div>
                                  <label className="text-xs text-slate-400 block mb-1">Profile Name *</label>
                                  <input
                                    type="text"
                                    value={profileForm.name}
                                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                                    placeholder="e.g., Tech Blog Voice"
                                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                                  />
                                </div>

                                {/* Description */}
                                <div>
                                  <label className="text-xs text-slate-400 block mb-1">Description</label>
                                  <input
                                    type="text"
                                    value={profileForm.description}
                                    onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                                    placeholder="Brief description of this brand voice"
                                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                                  />
                                </div>

                                {/* Industry & Tone Row */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-slate-400 block mb-1">Industry</label>
                                    <input
                                      type="text"
                                      value={profileForm.industry}
                                      onChange={(e) => setProfileForm({ ...profileForm, industry: e.target.value })}
                                      placeholder="e.g., Technology"
                                      className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-slate-400 block mb-1">Tone</label>
                                    <select
                                      value={profileForm.tone}
                                      onChange={(e) => setProfileForm({ ...profileForm, tone: e.target.value })}
                                      className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                                    >
                                      <option value="professional">Professional</option>
                                      <option value="casual">Casual</option>
                                      <option value="friendly">Friendly</option>
                                      <option value="authoritative">Authoritative</option>
                                      <option value="conversational">Conversational</option>
                                      <option value="formal">Formal</option>
                                    </select>
                                  </div>
                                </div>

                                {/* Target Audience */}
                                <div>
                                  <label className="text-xs text-slate-400 block mb-1">Target Audience</label>
                                  <input
                                    type="text"
                                    value={profileForm.target_audience}
                                    onChange={(e) => setProfileForm({ ...profileForm, target_audience: e.target.value })}
                                    placeholder="e.g., Small business owners, marketers"
                                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                                  />
                                </div>

                                {/* Keywords */}
                                <div>
                                  <label className="text-xs text-slate-400 block mb-1">Keywords (comma-separated)</label>
                                  <input
                                    type="text"
                                    value={profileForm.keywords}
                                    onChange={(e) => setProfileForm({ ...profileForm, keywords: e.target.value })}
                                    placeholder="e.g., AI, automation, productivity"
                                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                                  />
                                </div>

                                {/* White-label: Custom Footer */}
                                <div className="pt-2 border-t border-slate-700">
                                  <label className="text-xs text-slate-400 block mb-1">
                                    Custom Footer Text <span className="text-purple-400">(White-Label)</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={profileForm.custom_footer_text}
                                    onChange={(e) => setProfileForm({ ...profileForm, custom_footer_text: e.target.value })}
                                    placeholder="e.g., Generated by Your Agency Name"
                                    className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                                  />
                                  <p className="text-[10px] text-slate-500 mt-1">
                                    Replaces "ContentSage" branding in PDF/HTML exports
                                  </p>
                                </div>

                                {/* White-label: Logo Upload */}
                                <div className="pt-2">
                                  <label className="text-xs text-slate-400 block mb-1">
                                    Brand Logo <span className="text-purple-400">(White-Label)</span>
                                  </label>

                                  {editingProfileId ? (
                                    <div className="space-y-2">
                                      {/* Logo Preview */}
                                      {logoPreview ? (
                                        <div className="flex items-center gap-3 p-2 bg-slate-900/50 rounded-lg border border-slate-700">
                                          <img
                                            src={logoPreview}
                                            alt="Logo preview"
                                            className="w-12 h-12 object-contain bg-white rounded"
                                          />
                                          <div className="flex-1">
                                            <p className="text-xs text-green-400">Logo uploaded</p>
                                            <button
                                              type="button"
                                              onClick={handleDeleteLogo}
                                              disabled={isUploadingLogo}
                                              className="text-xs text-red-400 hover:text-red-300"
                                            >
                                              Remove logo
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div
                                          className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center cursor-pointer hover:border-purple-500 transition-colors"
                                          onClick={() => logoInputRef.current?.click()}
                                        >
                                          {isUploadingLogo ? (
                                            <Loader2 className="w-6 h-6 text-purple-400 animate-spin mx-auto" />
                                          ) : (
                                            <>
                                              <ImageIcon className="w-6 h-6 text-slate-500 mx-auto mb-1" />
                                              <p className="text-xs text-slate-400">Click to upload logo</p>
                                              <p className="text-[10px] text-slate-500">PNG, JPEG, SVG, WebP (max 2MB)</p>
                                            </>
                                          )}
                                        </div>
                                      )}

                                      {/* Hidden file input */}
                                      <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                                        className="hidden"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0];
                                          if (file) handleLogoUpload(file);
                                          e.target.value = '';
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <p className="text-xs text-slate-500 p-2 bg-slate-900/30 rounded border border-slate-700">
                                      Save the profile first to upload a logo
                                    </p>
                                  )}

                                  <p className="text-[10px] text-slate-500 mt-1">
                                    Appears at the top of PDF/HTML exports
                                  </p>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-end gap-2 pt-2">
                                  <button
                                    onClick={handleCancelProfileForm}
                                    className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleSaveProfile}
                                    disabled={isSavingProfile || !profileForm.name.trim()}
                                    className="px-4 py-1.5 text-sm bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded transition-colors flex items-center gap-2"
                                  >
                                    {isSavingProfile && <Loader2 className="w-3 h-3 animate-spin" />}
                                    {editingProfileId ? 'Save Changes' : 'Create Profile'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {isLoadingProfiles ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                            </div>
                          ) : brandProfiles.length === 0 && !showProfileForm ? (
                            <div className="text-center py-4">
                              <Building2 className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                              <p className="text-sm text-slate-400">No brand profiles yet</p>
                              <button
                                onClick={handleCreateNewProfile}
                                className="mt-2 text-sm text-purple-400 hover:text-purple-300"
                              >
                                Create your first profile
                              </button>
                            </div>
                          ) : brandProfiles.length > 0 && (
                            <div className="space-y-2">
                              {brandProfiles.map((profile) => (
                                <div
                                  key={profile.id}
                                  className={`p-3 rounded-lg border-2 transition-all ${
                                    selectedProfileId === profile.id
                                      ? 'bg-purple-900/30 border-purple-500 ring-1 ring-purple-500/50'
                                      : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div
                                      className="flex items-center gap-2 flex-1 cursor-pointer min-h-[36px]"
                                      onClick={() => handleSetDefaultProfile(
                                        selectedProfileId === profile.id ? null : profile.id
                                      )}
                                    >
                                      {/* Selection indicator - checkmark circle when selected */}
                                      <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                        selectedProfileId === profile.id
                                          ? 'bg-purple-500 border-purple-500'
                                          : 'border-slate-600 bg-transparent'
                                      }`}>
                                        {selectedProfileId === profile.id && (
                                          <Check className="w-3 h-3 text-white" />
                                        )}
                                      </div>
                                      <span className="font-medium text-white text-sm">{profile.name}</span>
                                      <span className="text-xs text-slate-500 capitalize">({profile.tone})</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleEditProfile(profile); }}
                                        className="p-1 hover:bg-slate-700 rounded transition-colors"
                                        title="Edit profile"
                                      >
                                        <Edit3 className="w-3.5 h-3.5 text-slate-400 hover:text-white" />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteProfile(profile.id); }}
                                        className="p-1 hover:bg-red-900/50 rounded transition-colors"
                                        title="Delete profile"
                                      >
                                        <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-400" />
                                      </button>
                                    </div>
                                  </div>
                                  {profile.description && (
                                    <p className="text-xs text-slate-400 mt-1 ml-7">{profile.description}</p>
                                  )}
                                  {(profile.logo_url || profile.custom_footer_text) && (
                                    <div className="flex items-center gap-2 mt-2 ml-7">
                                      {profile.logo_url && (
                                        <span className="text-[10px] bg-purple-900/50 text-purple-300 px-1.5 py-0.5 rounded">
                                          Logo
                                        </span>
                                      )}
                                      {profile.custom_footer_text && (
                                        <span className="text-[10px] bg-amber-900/50 text-amber-300 px-1.5 py-0.5 rounded">
                                          Custom Footer
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                              <p className="text-[10px] text-slate-500 mt-2">
                                Click a profile name to set as default for white-label exports.
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Status Cards */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* White-Label Status */}
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                      <div className="flex items-center gap-3 mb-2">
                        <Palette className="w-6 h-6 text-amber-400" />
                        <h3 className="font-semibold text-white">White-Label Exports</h3>
                      </div>
                      <p className="text-xs text-slate-400 mb-2">
                        PDFs and HTML downloads use your branding instead of ContentSage
                      </p>
                      {selectedProfileId && brandingSettings ? (
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-400" />
                          <span className="text-xs text-green-400">
                            Active: {brandProfiles.find(p => p.id === selectedProfileId)?.name}
                          </span>
                        </div>
                      ) : (
                        <p className="text-xs text-amber-400">
                          Select a brand profile above to enable
                        </p>
                      )}
                    </div>

                    {/* Priority Support */}
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                      <div className="flex items-center gap-3 mb-2">
                        <Users className="w-6 h-6 text-green-400" />
                        <h3 className="font-semibold text-white">Priority Support</h3>
                      </div>
                      <p className="text-xs text-slate-400 mb-2">
                        Dedicated support channel for agency accounts
                      </p>
                      <a
                        href="mailto:support@contentsage.app?subject=Agency Support"
                        className="text-xs text-green-400 hover:text-green-300 inline-flex items-center gap-1"
                      >
                        Contact support
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Features Preview - Show locked features for trial users */}
              {userData?.plan === 'free' ? (
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50 opacity-60">
                    <Lock className="w-6 h-6 text-slate-500 mb-3" />
                    <h3 className="font-semibold text-slate-300 mb-1">Bulk Generation</h3>
                    <p className="text-xs text-slate-400">
                      Generate multiple posts at once
                    </p>
                    <span className="text-xs text-purple-400 font-medium">Upgrade to unlock</span>
                  </div>
                  <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50 opacity-60">
                    <Lock className="w-6 h-6 text-slate-500 mb-3" />
                    <h3 className="font-semibold text-slate-300 mb-1">Knowledge Vault</h3>
                    <p className="text-xs text-slate-400">
                      Train AI on your documents
                    </p>
                    <span className="text-xs text-purple-400 font-medium">Upgrade to unlock</span>
                  </div>
                  <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50 opacity-60">
                    <Lock className="w-6 h-6 text-slate-500 mb-3" />
                    <h3 className="font-semibold text-slate-300 mb-1">Auto-Publish</h3>
                    <p className="text-xs text-slate-400">
                      Publish directly to your blog
                    </p>
                    <span className="text-xs text-purple-400 font-medium">Upgrade to unlock</span>
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                    <TrendingUp className="w-8 h-8 text-green-500 mb-3" />
                    <h3 className="font-semibold text-white mb-1">SEO Optimized</h3>
                    <p className="text-sm text-slate-400">
                      Every post is optimized for search engines
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                    <Clock className="w-8 h-8 text-blue-500 mb-3" />
                    <h3 className="font-semibold text-white mb-1">Save Hours</h3>
                    <p className="text-sm text-slate-400">
                      Generate in minutes, not hours
                    </p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                    <Star className="w-8 h-8 text-amber-500 mb-3" />
                    <h3 className="font-semibold text-white mb-1">Quality Content</h3>
                    <p className="text-sm text-slate-400">
                      Human-like, engaging blog posts
                    </p>
                  </div>
                </div>
              )}

              {/* View Plans CTA */}
              {userData?.plan === 'free' && (
                <div className="text-center">
                  <button
                    onClick={handleShowPricing}
                    className="text-purple-400 hover:text-purple-300 font-medium inline-flex items-center"
                  >
                    View all plans
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </button>
                </div>
              )}

            </motion.div>
          )}
        </AnimatePresence>

        {/* Preview Modal */}
        {previewContent !== null && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-5xl max-h-[90vh] flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-700">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <Eye className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Article Preview</h2>
                    <p className="text-sm text-slate-400">{previewTitle}</p>
                  </div>
                </div>
                <button
                  onClick={handleClosePreview}
                  className="p-2 bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700 hover:text-white transition-colors"
                  title="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-auto p-6">
                {isLoadingPreview ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                    <span className="ml-3 text-slate-400">Loading preview...</span>
                  </div>
                ) : (
                  <div
                    className="prose prose-invert prose-slate max-w-none"
                    dangerouslySetInnerHTML={{ __html: previewContent }}
                  />
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end p-6 border-t border-slate-700 space-x-3">
                <button
                  onClick={handleClosePreview}
                  className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
}

export default function WixDashboardPage() {
  return (
    <WixProvider>
      <WixDashboardContent />
    </WixProvider>
  );
}
